package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.service.ScreenConfigApi
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.Event
import kotlin.math.floor

private const val VIEW_POPUP = "Popup"
private const val VIEW_CONTENT = "Content"
private const val SCREEN_ROW_GRID = "170px 110px 80px"
private const val SCREEN_COL_W = 390
private const val SCREEN_BLOCK_GAP = 32
private const val SCREEN_HORIZONTAL_BUFFER = 56

private val SCREEN_MODE_OPTIONS = listOf(
    All(key = VIEW_POPUP, label = VIEW_POPUP),
    All(key = VIEW_CONTENT, label = VIEW_CONTENT)
)

private fun screenTyp(key: String): String =
    if (key.contains("Mobile", ignoreCase = true)) "Mobile" else "Desktop"

private fun modeToDisplay(mode: String): String =
    if (mode == ScreenMode.POPUP) VIEW_POPUP else VIEW_CONTENT

private fun displayToMode(value: String): String =
    if (value == VIEW_POPUP) ScreenMode.POPUP else ScreenMode.CONTENT

@Composable
fun DashboardAnzeigeArt() {
    Style(cssRules = SharedWorkspaceStyle.cssRules)

    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    var viewportWidth by remember { mutableStateOf(window.innerWidth.toDouble()) }
    val allScreens = remember {
        ScreenManager.allIndexKeys()
            .filterNot { it == "KontaktKalender" }
            .sortedBy { ScreenManager.labelForKey(it).lowercase() }
            .map { key -> All(key = key, label = ScreenManager.labelForKey(key)) }
    }

    var config by remember { mutableStateOf<Map<String, Map<String, String>>>(emptyMap()) }
    var search by remember { mutableStateOf("") }
    val screenConfigVersion = ScreenConfigApi.version()

    suspend fun reloadConfig() {
        config = ScreenConfigApi.loadScreenConfig()
    }

    LaunchedEffect(screenConfigVersion) {
        reloadConfig()
    }

    DisposableEffect(Unit) {
        val listener: (Event) -> Unit = {
            viewportWidth = window.innerWidth.toDouble()
        }
        window.addEventListener("resize", listener)
        onDispose { window.removeEventListener("resize", listener) }
    }

    fun setMode(key: String, display: String) {
        config = config + (key to ((config[key] ?: emptyMap()) + ("mode" to displayToMode(display))))
    }

    fun setAllModes(mode: String) {
        config = allScreens.fold(config) { acc, entry ->
            acc + (entry.key to ((acc[entry.key] ?: emptyMap()) + ("mode" to mode)))
        }
    }

    val filteredScreens = if (search.isBlank()) allScreens
    else allScreens.filter {
        it.label.contains(search, ignoreCase = true) || it.key.contains(search, ignoreCase = true)
    }

    val availableWidth = (
        viewportWidth -
            ContentOffset.LEFT.value -
            ContentOffset.RIGHT.value -
            SCREEN_HORIZONTAL_BUFFER
        ).coerceAtLeast(SCREEN_COL_W.toDouble())
    val columnWidthWithGap = SCREEN_COL_W + SCREEN_BLOCK_GAP
    val dynamicColumnCount = floor(
        (availableWidth + SCREEN_BLOCK_GAP) / columnWidthWithGap.toDouble()
    ).toInt().coerceAtLeast(1)
    val columnCount = dynamicColumnCount.coerceAtMost(filteredScreens.size.coerceAtLeast(1))
    val columns = if (filteredScreens.isEmpty()) listOf(emptyList())
    else filteredScreens.chunked(((filteredScreens.size + columnCount - 1) / columnCount).coerceAtLeast(1))

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    flex(1)
                    minHeight(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Screenverwaltung") }
                    }
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        gap(22.px)
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Input(InputType.Text) {
                        classes(SharedWorkspaceStyle.textInput)
                        value(search)
                        placeholder("Screen suchen...")
                        onInput { search = it.value }
                        style { width(220.px) }
                    }
                    LumaticaButton(
                        label = "Speichern",
                        onClick = {
                            scope.launch {
                                val saved = ScreenConfigApi.saveScreenConfig(config)
                                config = saved
                                ScreenManager.applyScreenConfig(saved)
                                org.example.project.service.GlobalManager.triggerSessionConfigReload()
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Popup",
                        onClick = { setAllModes(ScreenMode.POPUP) },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Alle Content",
                        onClick = { setAllModes(ScreenMode.CONTENT) },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Refresh",
                        onClick = { scope.launch { reloadConfig() } },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                }

                Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                Div({
                    classes(SharedWorkspaceStyle.columnList)
                    style {
                        flex(1)
                        minHeight(0.px)
                        overflowY("auto")
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            width(100.percent)
                            property("grid-template-columns", "repeat($columnCount, minmax(0, 1fr))")
                            gap(SCREEN_BLOCK_GAP.px)
                            boxSizing("border-box")
                        }
                    }) {
                        columns.forEachIndexed { index, columnKeys ->
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    minWidth(0.px)
                                    if (index < columns.lastIndex) {
                                        property("border-right", "1px solid rgba(255,255,255,0.07)")
                                        paddingRight(20.px)
                                    }
                                }
                            }) {
                                Div({
                                    classes(SharedWorkspaceStyle.permissionHeader)
                                    style { property("grid-template-columns", SCREEN_ROW_GRID) }
                                }) {
                                    Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text("Screenname") }
                                    Span { Text("Ansicht") }
                                    Span { Text("Typ") }
                                }
                                columnKeys.forEach { entry ->
                                    val key = entry.key
                                    val currentMode = modeToDisplay(
                                        config[key]?.get("mode")?.takeIf { it.isNotBlank() }
                                            ?: ScreenManager.defaultScreenModeForKey()
                                    )
                                    Div({
                                        classes(SharedWorkspaceStyle.permissionRow)
                                        style { property("grid-template-columns", SCREEN_ROW_GRID) }
                                    }) {
                                        Span({ classes(SharedWorkspaceStyle.permissionName) }) { Text(entry.label) }
                                        AppDropdown(
                                            options = SCREEN_MODE_OPTIONS,
                                            selectedValue = currentMode,
                                            onChange = { setMode(key, it) },
                                            appearance = AppDropdownAppearance.Underline,
                                            attrs = {
                                                classes(SharedWorkspaceStyle.permissionSelect)
                                                style { width(100.px) }
                                            }
                                        )
                                        Span({
                                            style {
                                                fontSize(13.px)
                                                color(Color.white)
                                            }
                                        }) { Text(screenTyp(key)) }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
